import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack
} from '@mui/material';
import { UploadFile } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const DOC_CATEGORIES = [
  { value: 'CRS', label: 'CRS' },
  { value: 'CAME_FORM', label: 'CAME Form' },
  { value: 'WORK_PACKAGE', label: 'Work Package' },
  { value: 'OTHER', label: 'Other' }
];

const ExternalAgencyActions = ({ open, onClose, workOrder, onSuccess }) => {
  const [category, setCategory] = useState('CRS');
  const [notes, setNotes] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (!open || !workOrder) return;
    setDocuments(Array.isArray(workOrder.externalAgencyDocuments) ? workOrder.externalAgencyDocuments : []);
    setCategory('CRS');
    setNotes('');
    setCompleteNotes('');
    setFiles([]);
    setError('');
  }, [open, workOrder]);

  const handleUpload = async () => {
    if (!files.length) {
      setError('Select at least one file to upload');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const formData = new FormData();
      formData.append('category', category);
      if (notes.trim()) formData.append('notes', notes.trim());
      files.forEach((file) => formData.append('files', file));

      const { data } = await axios.post(
        `/api/maintenance/${workOrder._id}/external-agency/documents`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDocuments(Array.isArray(data.externalAgencyDocuments) ? data.externalAgencyDocuments : []);
      setFiles([]);
      setNotes('');
      showToast('Document(s) uploaded successfully', 'success');
      onSuccess?.();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to upload documents';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!documents.length) {
      setError('Upload at least one document (CRS, CAME form, or other) before submitting to CAMO QM');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await axios.post(`/api/maintenance/${workOrder._id}/external-agency/complete`, {
        notes: completeNotes.trim() || undefined
      });
      showToast('External agency work submitted. CAMO QM completion acknowledgment is required next.', 'success');
      onSuccess?.();
      handleClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to complete external agency action';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setFiles([]);
    setNotes('');
    setCompleteNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>External Agency Action</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            External Agency:{' '}
            <strong>
              {workOrder?.externalAgencyName
                || workOrder?.externalAgencyId?.name
                || 'Not assigned'}
            </strong>
            {workOrder?.externalAgencyCode || workOrder?.externalAgencyId?.code
              ? ` (${workOrder?.externalAgencyCode || workOrder?.externalAgencyId?.code})`
              : ''}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Upload CRS, CAME form, and other documents received from the external organisation.
          When ready, submit to return this work order to the CAMO QM completion acknowledgment flow.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" gutterBottom>
          Uploaded documents ({documents.length})
        </Typography>
        {documents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No documents uploaded yet.
          </Typography>
        ) : (
          <List dense sx={{ mb: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            {documents.map((doc) => (
              <ListItem
                key={doc._id || `${doc.fileUrl}-${doc.uploadedAt}`}
                secondaryAction={
                  doc.fileUrl ? (
                    <Button size="small" href={doc.fileUrl} target="_blank" rel="noopener noreferrer">
                      View
                    </Button>
                  ) : null
                }
              >
                <ListItemText
                  primary={doc.originalName || doc.fileName}
                  secondary={doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : undefined}
                />
                <Chip size="small" label={doc.category || 'OTHER'} sx={{ mr: 1 }} />
              </ListItem>
            ))}
          </List>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="external-doc-category-label">Document type</InputLabel>
            <Select
              labelId="external-doc-category-label"
              label="Document type"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {DOC_CATEGORIES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFile />}
            sx={{ minWidth: 200 }}
          >
            Choose files
            <input
              hidden
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </Button>
        </Stack>
        {files.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Selected: {files.map((f) => f.name).join(', ')}
          </Typography>
        )}
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Upload notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={loading || files.length === 0}
          sx={{ mb: 3 }}
        >
          {loading ? 'Uploading…' : 'Upload documents'}
        </Button>

        <TextField
          fullWidth
          multiline
          rows={3}
          label="Completion notes (optional)"
          value={completeNotes}
          onChange={(e) => setCompleteNotes(e.target.value)}
          placeholder="Summarise external agency work before CAMO QM review..."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleComplete}
          disabled={loading || documents.length === 0}
        >
          {loading ? 'Submitting…' : 'Submit to CAMO QM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExternalAgencyActions;
